import React, { useState } from 'react';
import { ShieldAlert, Sparkles, X } from 'lucide-react';

/**
 * Ebeveyn/Vasi onay pop-up'ı — Çocuk Modu'na ilk geçişte tek seferlik gösterilir.
 * Kabul edilmeden Çocuk Modu'na geçilemez. localStorage'a kaydedilir.
 */
const KEY = 'hafizim_kids_parental_consent_v1';

export const hasParentalConsent = () => {
  try {
    return !!localStorage.getItem(KEY);
  } catch (_) { return false; }
};

const ParentalConsentModal = ({ open, onAccept, onCancel }) => {
  const [acknowledged, setAcknowledged] = useState(false);

  if (!open) return null;

  const accept = () => {
    if (!acknowledged) return;
    try {
      localStorage.setItem(KEY, JSON.stringify({ accepted: true, ts: Date.now() }));
    } catch (_) { /* ignore */ }
    onAccept?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="parental-consent-modal"
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-br from-amber-400 to-amber-600 p-6 text-white relative">
          <button
            type="button"
            onClick={onCancel}
            aria-label="Kapat"
            data-testid="parental-consent-close"
            className="absolute right-4 top-4 p-1.5 rounded-lg hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mb-3">
            <ShieldAlert className="w-7 h-7" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ebeveyn / Vasi Onayı
          </h2>
          <p className="text-amber-50 text-sm mt-1">Çocuk Modu&apos;na geçmeden önce</p>
        </div>

        <div className="p-6 space-y-4 text-stone-800">
          <p className="text-sm leading-relaxed">
            <strong>Çocuk Modu</strong>, 13 yaşın altındaki kullanıcılar için <strong>ebeveyn veya yasal vasi gözetiminde</strong> kullanılmak üzere tasarlanmıştır.
            Bu moda geçerek aşağıdakileri kabul etmiş olursunuz:
          </p>

          <ul className="text-sm space-y-2 bg-amber-50/60 rounded-2xl p-4 list-disc pl-7">
            <li><strong>18 yaşından büyüğüm</strong> veya bu hesabı yöneten bir ebeveyn/yasal vasiyim.</li>
            <li>Çocuğum tarafından kullanılan içeriği <strong>denetleyeceğim</strong>.</li>
            <li>Mikrofon kullanımı, ilerleme kaydı ve dini eğitim içeriği hakkında <strong>çocuğumu bilgilendirdim</strong>.</li>
            <li>13 yaş altı kullanıcının verileri toplanırken KVKK ve KOSGEB Çocuk Hakları çerçevesinde sorumlu davranacağım.</li>
          </ul>

          <p className="text-xs text-stone-500 leading-relaxed">
            Detaylar için <a href="/gizlilik-politikasi" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline">Gizlilik Politikası</a> bölüm 7 (Çocukların Verileri).
          </p>

          <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-xl border-2 border-stone-200 hover:border-amber-400 transition-colors" data-testid="parental-consent-checkbox-label">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="parental-consent-checkbox"
              className="mt-1 w-5 h-5 rounded border-stone-300 accent-amber-500"
            />
            <span className="text-sm font-medium text-stone-800">
              Yukarıdaki tüm maddeleri okudum ve <strong>ebeveyn/vasi sıfatıyla</strong> kabul ediyorum.
            </span>
          </label>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              data-testid="parental-consent-cancel"
              className="flex-1 px-4 py-3 rounded-xl border border-stone-200 text-stone-700 text-sm font-semibold hover:bg-stone-50 transition-colors"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={accept}
              disabled={!acknowledged}
              data-testid="parental-consent-accept"
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg transition-all inline-flex items-center justify-center gap-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <Sparkles className="w-4 h-4" /> Çocuk Modu&apos;nu Aç
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentalConsentModal;
